import React from 'react';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';
import { useNovaTheme } from './NovaThemeContext';

// Токены Nova. Recharts не читает CSS-переменные в defs,
// поэтому палитры продублированы здесь — по одной на режим.
const PALETTES = {
    dark: {
        accent: '#7B7FF2',
        good: '#3ECF8E',
        bad: '#F06A6A',
        surface: '#1D2026',
        hairline: 'rgba(255, 255, 255, 0.12)',
        ink: '#EEF0F6',
        inkMuted: '#99A0AD',
        grid: 'rgba(255, 255, 255, 0.09)',
        fillFrom: 'rgba(123, 127, 242, 0.30)',
        fillTo: 'rgba(123, 127, 242, 0)',
        tooltipShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.05), 0 18px 44px -12px rgba(0, 0, 0, 0.6)',
    },
    light: {
        accent: '#5B60E8',
        good: '#17A56D',
        bad: '#D94C4C',
        surface: '#FFFFFF',
        hairline: 'rgba(16, 19, 26, 0.12)',
        ink: '#1B1F27',
        inkMuted: '#5A6272',
        grid: 'rgba(16, 19, 26, 0.1)',
        fillFrom: 'rgba(91, 96, 232, 0.22)',
        fillTo: 'rgba(91, 96, 232, 0)',
        tooltipShadow: '0 18px 44px -12px rgba(23, 28, 40, 0.25)',
    },
};

const NovaTooltip = ({ active, payload, currencyFormat, labels = {}, p }) => {
    if (!active || !payload || !payload.length) return null;
    const current = payload[0].value || 0;
    const first = payload[0].payload.chartData?.[0]?.balance || 0;
    const pct = first > 0 ? ((current - first) / first) * 100 : 0;
    const positive = pct >= 0;

    const row = { display: 'flex', justifyContent: 'space-between', gap: 24, alignItems: 'baseline' };
    const label = { fontSize: 11, fontWeight: 500, color: p.inkMuted };

    return (
        <div style={{
            background: p.surface,
            border: `1px solid ${p.hairline}`,
            borderRadius: 12,
            padding: '11px 13px',
            minWidth: 180,
            boxShadow: p.tooltipShadow,
            fontFamily: 'Inter, system-ui, sans-serif',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
        }}>
            <div style={row}>
                <span style={label}>{labels.balance || 'Balance'}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: p.ink, fontVariantNumeric: 'tabular-nums' }}>
                    {currencyFormat} {(current || 0).toLocaleString()}
                </span>
            </div>
            <div style={row}>
                <span style={label}>{labels.change || 'Change'}</span>
                <span style={{ fontSize: 12, fontWeight: 550, color: positive ? p.good : p.bad, fontVariantNumeric: 'tabular-nums' }}>
                    {positive ? '+' : ''}{pct.toFixed(1)}%
                </span>
            </div>
            <div style={{ ...row, paddingTop: 6, borderTop: `1px solid ${p.hairline}` }}>
                <span style={label}>{labels.initial || 'Initial'}</span>
                <span style={{ fontSize: 11, color: p.inkMuted, fontVariantNumeric: 'tabular-nums' }}>
                    {currencyFormat} {(first || 0).toLocaleString()}
                </span>
            </div>
        </div>
    );
};

/**
 * NovaChart — график портфеля в дизайн-системе Nova:
 * одна акцентная линия, тихая заливка, тултип и сетка в цветах текущего режима.
 * Заполняет высоту родителя.
 */
export default function NovaChart({ data, currencyFormat = 'RM', tooltipLabels }) {
    const { mode } = useNovaTheme();
    const p = PALETTES[mode] || PALETTES.dark;
    const dataWithRef = data.map(item => ({ ...item, chartData: data }));

    // Домен Y с небольшим запасом: при ['dataMin','dataMax'] нижние пики
    // ложатся ровно на границу области и линия срезается наполовину толщины
    const values = data.map((d) => d?.balance ?? 0);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const pad = (max - min) * 0.03 || Math.abs(max) * 0.01 || 1;
    const yDomain = [min - pad, max + pad];

    return (
        <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={dataWithRef} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                <defs>
                    {/* id зависит от режима, чтобы градиент пересоздавался при переключении */}
                    <linearGradient id={`novaBalanceFill-${mode}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={p.fillFrom} />
                        <stop offset="100%" stopColor={p.fillTo} />
                    </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={p.grid} />
                <XAxis dataKey="name" hide />
                <YAxis hide domain={yDomain} />
                <Tooltip content={<NovaTooltip currencyFormat={currencyFormat} labels={tooltipLabels} p={p} />} cursor={{ stroke: p.hairline, strokeDasharray: '3 3' }} />
                <Area
                    type="linear"
                    dataKey="balance"
                    stroke={p.accent}
                    strokeWidth={1.5}
                    fill={`url(#novaBalanceFill-${mode})`}
                    dot={false}
                    activeDot={{ r: 4, fill: p.accent, stroke: p.surface, strokeWidth: 2 }}
                />
            </AreaChart>
        </ResponsiveContainer>
    );
}
